"use client";

import { useEffect } from "react";

/**
 * Hard scroll-pause at key marketing sections.
 *
 * CSS scroll-snap (proximity + scroll-snap-stop:always) does NOT reliably stop a
 * fast fling in Chrome — a 1px snap target gets blown straight past. So instead we
 * read the `.scroll-stop` marker elements (placed at the start of the Reality demo,
 * the AI Assistant section, and the closing CTA) and, the first time the user
 * scrolls DOWN across each one, we pin the scroll there for a beat — cancelling
 * wheel + touch momentum — so the page genuinely lingers there at ANY scroll speed,
 * on both desktop and mobile. Each marker fires once per downward pass; scrolling
 * back up re-arms it.
 */
export function ScrollPauseController() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // Touch devices get only the markers that are safe there. A marker can opt
    // OUT of mobile with `data-pause-desktop-only` — used for the closing CTA,
    // whose hard-pin (preventing touchmove) jammed iOS momentum and could trap
    // the user before the footer. The CTA already has its own long sticky pin
    // for a mobile pause, so it doesn't need the hard freeze on touch.
    const isDesktop = window.matchMedia(
      "(min-width: 1024px) and (pointer: fine)",
    ).matches;

    const html = document.documentElement;

    type Stop = { y: number; dur: number; armed: boolean };
    let stops: Stop[] = [];

    const measure = () => {
      const markers = Array.from(
        document.querySelectorAll<HTMLElement>(".scroll-stop"),
      ).filter((m) => isDesktop || m.dataset.pauseDesktopOnly === undefined);
      stops = markers.map((m, i) => ({
        // Absolute document offset of the marker, minus a top offset. Defaults to
        // the 72px sticky header; a marker can set `data-pause-offset` to stop a
        // little higher (leaving breathing room above the section).
        y: Math.round(
          m.getBoundingClientRect().top +
            window.scrollY -
            (Number((m as HTMLElement).dataset.pauseOffset) || 72),
        ),
        // Long, deliberate pauses so the section is actually taken in; the
        // closing CTA (last marker) lingers longest.
        dur: i === markers.length - 1 ? 1900 : 1400,
        armed: true,
      }));
    };
    measure();

    let locked = false;
    let target = 0;
    let lastY = window.scrollY;
    let releaseTimer: ReturnType<typeof setTimeout> | undefined;

    const hold = () => {
      if (locked) html.scrollTop = target;
    };
    const prevent = (e: Event) => {
      if (locked) e.preventDefault();
    };

    const engage = (s: Stop) => {
      locked = true;
      target = s.y;
      const prevBehavior = html.style.scrollBehavior;
      // Smooth behavior would animate (and fight) the pin — pin instantly.
      html.style.scrollBehavior = "auto";
      html.scrollTop = target;
      window.addEventListener("scroll", hold, { passive: true });
      window.addEventListener("wheel", prevent, { passive: false });
      window.addEventListener("touchmove", prevent, { passive: false });
      releaseTimer = setTimeout(() => {
        window.removeEventListener("scroll", hold);
        window.removeEventListener("wheel", prevent);
        window.removeEventListener("touchmove", prevent);
        html.style.scrollBehavior = prevBehavior;
        lastY = window.scrollY;
        locked = false;
      }, s.dur);
    };

    const onScroll = () => {
      if (locked) return;
      const y = window.scrollY;
      // Re-arm any stop the user has scrolled back above.
      for (const s of stops) if (y < s.y - 8) s.armed = true;
      if (y > lastY) {
        for (const s of stops) {
          if (s.armed && lastY < s.y && y >= s.y) {
            s.armed = false;
            engage(s);
            break;
          }
        }
      }
      lastY = y;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measure);
    // Re-measure once layout/fonts/images have settled (marker offsets shift).
    const settleTimer = setTimeout(measure, 900);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", hold);
      window.removeEventListener("wheel", prevent);
      window.removeEventListener("touchmove", prevent);
      clearTimeout(settleTimer);
      if (releaseTimer) clearTimeout(releaseTimer);
      html.style.scrollBehavior = "";
    };
  }, []);

  return null;
}
